import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiDownload, FiShoppingBag, FiBookOpen, FiArrowRight, FiMail, FiTag, FiFileText, FiGrid } from 'react-icons/fi';
import WhatsAppJoinRow from './WhatsAppJoinRow';
import { NextStepBar, WhatsAppBody, WhatsAppCard, WhatsAppList, StepsIntro, StepBlock, StepHeading, StepNumber, StepText, StepButton } from './WhatsAppJoin.styles';
import { formatCourseRange, formatShortDate, hasCourseStarted, parseCourseDate } from './courseCatalogHelpers';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { clearSavedCart } from './courseCatalogHelpers';
import { Container, PageStack, Card, Hero, HeroIcon, Headline, HeroSubtitle, EmailNotice, CardTitle, MutedText, SummaryHeader, MetadataGrid, MetadataTile, TileLabel, TileValue, PromoPill, LineItems, LineRow, LineLabel, ItemDetails, ItemName, ItemMeta, LineValue, TotalRow, TotalValue, ButtonGroup, ActionButton } from './PurchaseConfirmation.styles';

const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

const formatCourseDate = (dateString) => {
  if (!dateString) return null;

  const datePart = String(dateString).split('T')[0];
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return null;

  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getCourseDateRange = (course) => {
  const startDate = formatCourseDate(course.startDate || course.start_date);
  const endDate = formatCourseDate(course.endDate || course.end_date);

  if (startDate && endDate) return `${startDate} - ${endDate}`;
  if (startDate) return `Starts ${startDate}`;
  if (endDate) return `Ends ${endDate}`;
  return null;
};

// "SHM101 · Starts Oct 21" (or "Started Oct 21" once it is under way); just the code when there are no dates.
const courseJoinMeta = (course) => {
  const start = parseCourseDate(course.startDate || course.start_date);
  const when = start ? `${hasCourseStarted(course) ? 'Started' : 'Starts'} ${formatShortDate(start)}` : null;
  return [course.code, when].filter(Boolean).join(' · ');
};

const getInstructorName = (course) => {
  const directInstructor = course.instructor || course.instructorName || course.instructor_name;
  if (directInstructor) return directInstructor;

  return `${course.instructorFirstName || course.instructor_first_name || ''} ${course.instructorLastName || course.instructor_last_name || ''}`.trim();
};

// The last confirmation shown is kept for this browser tab, so a new student who opens "Set my password" and closes it
// (or refreshes) lands back on their confirmation instead of "No purchase data". Cleared when the tab closes.
const LAST_PURCHASE_KEY = 'faithinsight_last_purchase_confirmation';
const saveLastPurchase = (data) => {
  try { window.sessionStorage.setItem(LAST_PURCHASE_KEY, JSON.stringify(data)); } catch (e) { /* storage unavailable */ }
};
const readLastPurchase = (sessionEmail) => {
  try {
    const data = JSON.parse(window.sessionStorage.getItem(LAST_PURCHASE_KEY) || 'null');
    const email = String(data?.user?.email || '').toLowerCase();
    // only ever the signed-in student's own confirmation
    return data && email && email === String(sessionEmail || '').toLowerCase() ? data : undefined;
  } catch (e) {
    return undefined;
  }
};

function PurchaseConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: sessionUser } = useAuth();
  const [purchaseData, setPurchaseData] = useState(
    () => location.state?.purchaseData || (searchParams.get('payment_intent') ? undefined : readLastPurchase(sessionUser?.email))
  );
  const [loading, setLoading] = useState(!purchaseData);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  // The purchase went through, so the catalog cart that led here is spent.
  useEffect(() => {
    if (purchaseData) {
      clearSavedCart();
      saveLastPurchase(purchaseData);
    }
  }, [purchaseData]);

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollToTop();
    const frameId = requestAnimationFrame(scrollToTop);
    const timeoutId = window.setTimeout(scrollToTop, 50);

    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!purchaseData && !hasFetchedRef.current) {
      const paymentIntentId = searchParams.get('payment_intent');

      if (paymentIntentId) {
        hasFetchedRef.current = true;
        setLoading(true);
        setError(null);

        const redirectStatus = searchParams.get('redirect_status');
        if (redirectStatus === 'failed') {
          setError('Payment was declined. Please try a different payment method or contact your bank.');
          setLoading(false);
          return;
        }

        api.getPurchaseConfirmation(paymentIntentId)
          .then((data) => {
            if (data.error) {
              setError(data.message || 'Payment was not successful. Please try again.');
              setLoading(false);
            } else {
              setPurchaseData(data);
              setLoading(false);
              // Opened from a ?payment_intent=... link (a new student's return from checkout, or a redirect-based
              // payment method): keep the data and take the payment details out of the address bar, so it reads
              // /confirmation like it does for a signed-in student.
              navigate(location.pathname, { replace: true, state: { purchaseData: data } });
            }
          })
          .catch((err) => {
            console.error('Failed to fetch purchase confirmation:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load purchase confirmation';
            const paymentStatus = err.response?.data?.paymentStatus;

            if (paymentStatus === 'failed' || errorMessage.includes('declined') || errorMessage.includes('failed')) {
              setError('Payment was declined. Please try a different payment method or contact your bank.');
            } else {
              setError(errorMessage);
            }
            setLoading(false);
            hasFetchedRef.current = false;
          });
      } else {
        setLoading(false);
      }
    }
  }, [purchaseData, searchParams, navigate, location.pathname]);

  if (loading) {
    return (
      <Container>
        <PageStack initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Hero>
            <HeroSubtitle>Loading purchase confirmation...</HeroSubtitle>
          </Hero>
        </PageStack>
      </Container>
    );
  }

  if (error || !purchaseData) {
    return (
      <Container>
        <PageStack initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Hero>
            <HeroSubtitle>{error || 'No purchase data found. Please try again.'}</HeroSubtitle>
          </Hero>
          <ButtonGroup>
            <ActionButton $primary onClick={() => navigate('/catalog')}>
              <FiShoppingBag size={16} />
              Browse Course Catalog
            </ActionButton>
          </ButtonGroup>
        </PageStack>
      </Container>
    );
  }

  if (!purchaseData.user || !purchaseData.payment) {
    return (
      <Container>
        <PageStack initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Hero>
            <HeroSubtitle>Purchase data is incomplete. Please contact support if this issue persists.</HeroSubtitle>
          </Hero>
          <ButtonGroup>
            <ActionButton $primary onClick={() => navigate('/catalog')}>
              <FiShoppingBag size={16} />
              Browse Course Catalog
            </ActionButton>
          </ButtonGroup>
        </PageStack>
      </Container>
    );
  }

  const { user, products, courses, payment, promoCode } = purchaseData;
  const totalAmount = parseFloat(payment.amount || 0);
  const isCourseEnrollment = courses && courses.length > 0;
  const items = isCourseEnrollment ? courses : products || [];
  const paymentStatus = String(payment.status || purchaseData.status || '').toLowerCase();
  const isFreeEnrollment = purchaseData.freeEnrollment === true || totalAmount === 0;
  // A confirmation is only returned for a payment that went through; a response with no status at all (such as the
  // one opened from a ?payment_intent= link) still counts. Only an explicit non-success status hides the card.
  const hasSuccessfulPayment = !paymentStatus || ['succeeded', 'success', 'completed', 'complete', 'paid'].includes(paymentStatus);
  const canShowWhatsAppLinks = isCourseEnrollment && (hasSuccessfulPayment || isFreeEnrollment);
  const coursesWithWhatsAppLinks = canShowWhatsAppLinks
    ? courses.filter(course => {
        const link = course.whatsappGroupLink || course.whatsapp_group_link;
        return typeof link === 'string' && link.trim().length > 0;
      })
    : [];

  // A new student (no password yet) gets one combined "Next steps" card: set a password, then join the group(s).
  const needsPassword = Boolean(sessionUser?.password_change_required);
  const actionCount = 1 + (coursesWithWhatsAppLinks.length > 0 ? 1 : 0);
  const whatsAppRows = (
    <WhatsAppList>
      {coursesWithWhatsAppLinks.map(course => {
        const link = (course.whatsappGroupLink || course.whatsapp_group_link).trim();
        return (
          <WhatsAppJoinRow key={`${course.id}-${course.code}`} course={course} link={link} meta={courseJoinMeta(course)} />
        );
      })}
    </WhatsAppList>
  );

  const customerName = `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim();
  const primaryCourseName = isCourseEnrollment
    ? courses.length === 1 ? courses[0].name : `${courses.length} courses`
    : items.length === 1 ? items[0].name : `${items.length} items`;
  const orderNumber = payment.orderNumber || (totalAmount === 0 ? 'Completed' : 'Pending');
  // Lines under "You're enrolled!": each course with its own dates ("Shama'il · Oct 21 – Dec 9, 2026"). When none of the
  // courses has dates (or it is not a course order) fall back to the single "name · Order #" line.
  const courseDates = (course) => formatCourseRange({
    startDate: course.startDate || course.start_date,
    endDate: course.endDate || course.end_date,
  });
  const datedCourses = isCourseEnrollment && courses.some((course) => courseDates(course));
  const heroLines = datedCourses
    ? courses.map((course) => [course.name, courseDates(course)].filter(Boolean).join(' · '))
    : [`${primaryCourseName} · Order ${orderNumber}`];
  const purchaseDate = payment.purchaseDate ? new Date(payment.purchaseDate).toLocaleDateString() : new Date().toLocaleDateString();
  const originalSubtotal = items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
  const discountAmount = Math.max(0, originalSubtotal - totalAmount);
  const hasDiscount = discountAmount > 0.01;

  const handleDownloadReceipt = () => {
    if (payment.receiptUrl) {
      window.open(payment.receiptUrl, '_blank');
    }
  };

  return (
    <Container>
      <PageStack initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Hero>
          <HeroIcon>
            <FiCheckCircle size={28} />
          </HeroIcon>
          <Headline>You're enrolled!</Headline>
          {heroLines.map((line, index) => <HeroSubtitle key={`${index}-${line}`}>{line}</HeroSubtitle>)}
        </Hero>

        <EmailNotice>
          <FiMail size={16} />
          <span>Confirmation sent to <strong>{user.email || 'your email address'}</strong></span>
        </EmailNotice>

        {needsPassword && (
          <WhatsAppCard>
            <NextStepBar>
              <span className="label"><FiArrowRight size={14} aria-hidden="true" /> Next steps</span>
              <span>{actionCount} {actionCount === 1 ? 'action' : 'actions'} needed</span>
            </NextStepBar>
            <WhatsAppBody>
              <StepsIntro>
                Your enrollment is confirmed. {actionCount === 1 ? 'Finish this step' : 'Finish these two steps'} before class starts.
              </StepsIntro>
              <StepBlock>
                <StepHeading><StepNumber>1</StepNumber>Set your password</StepHeading>
                <StepText>Activate your account to access your courses.</StepText>
                <StepButton onClick={() => navigate('/dashboard', { state: { fromConfirmation: true } })}>Set my password</StepButton>
              </StepBlock>
              {coursesWithWhatsAppLinks.length > 0 && (
                <StepBlock>
                  <StepHeading><StepNumber>2</StepNumber>Join your course WhatsApp group{coursesWithWhatsAppLinks.length > 1 ? 's' : ''}</StepHeading>
                  <StepText>Receive important class updates and announcements.</StepText>
                  {whatsAppRows}
                </StepBlock>
              )}
            </WhatsAppBody>
          </WhatsAppCard>
        )}

        {!needsPassword && coursesWithWhatsAppLinks.length > 0 && (
          <WhatsAppCard>
            <NextStepBar>
              <span className="label"><FiArrowRight size={14} aria-hidden="true" /> Next step</span>
              <span>{coursesWithWhatsAppLinks.length} {coursesWithWhatsAppLinks.length === 1 ? 'action' : 'actions'} needed</span>
            </NextStepBar>
            <WhatsAppBody>
            <div>
              <CardTitle>Join your course WhatsApp group</CardTitle>
              <MutedText>Please join the WhatsApp group for each enrolled course to receive important class updates and announcements.</MutedText>
            </div>
            {whatsAppRows}
            </WhatsAppBody>
          </WhatsAppCard>
        )}

        <Card>
          <SummaryHeader>
            <FiFileText size={13} />
            Order summary
          </SummaryHeader>
          <MetadataGrid>
            <MetadataTile>
              <TileLabel>Order no.</TileLabel>
              <TileValue>{orderNumber}</TileValue>
            </MetadataTile>
            <MetadataTile>
              <TileLabel>Date</TileLabel>
              <TileValue>{purchaseDate}</TileValue>
            </MetadataTile>
            <MetadataTile>
              <TileLabel>Name</TileLabel>
              <TileValue>{customerName || 'N/A'}</TileValue>
            </MetadataTile>
            <MetadataTile>
              <TileLabel>Promo code</TileLabel>
              <TileValue>
                {promoCode ? (
                  <PromoPill>
                    <FiTag size={13} />
                    {promoCode.code}
                  </PromoPill>
                ) : 'None'}
              </TileValue>
            </MetadataTile>
          </MetadataGrid>

          <LineItems>
            {items.map((item, index) => {
              const instructorName = isCourseEnrollment ? getInstructorName(item) : null;
              const courseDateRange = isCourseEnrollment ? getCourseDateRange(item) : null;

              return (
                <LineRow key={`${item.id || index}-${item.code || item.name}`}>
                  <LineLabel>
                    <ItemDetails>
                      <ItemName>{item.name}</ItemName>
                      {isCourseEnrollment && (item.code || instructorName) && (
                        <ItemMeta>{`${item.code || 'N/A'} • ${instructorName || 'TBA'}`}</ItemMeta>
                      )}
                      {courseDateRange && <ItemMeta>{courseDateRange}</ItemMeta>}
                    </ItemDetails>
                  </LineLabel>
                  <LineValue $accent={parseFloat(item.price || 0) === 0}>
                    {parseFloat(item.price || 0) === 0 ? 'Free' : formatCurrency(item.price)}
                  </LineValue>
                </LineRow>
              );
            })}

            {hasDiscount && (
              <LineRow>
                <LineLabel>Discount{promoCode ? ` (${promoCode.code})` : ''}</LineLabel>
                <LineValue $accent>-{formatCurrency(discountAmount)}</LineValue>
              </LineRow>
            )}

            <TotalRow>
              <LineLabel>Total</LineLabel>
              <TotalValue>{totalAmount === 0 ? 'Free' : formatCurrency(totalAmount)}</TotalValue>
            </TotalRow>
          </LineItems>
        </Card>

        <ButtonGroup>
          <ActionButton $primary onClick={() => navigate('/courses', { state: { refresh: true } })}>
            <FiBookOpen size={16} />
            Access My Courses
          </ActionButton>
          <ActionButton onClick={() => navigate('/catalog')}>
            <FiGrid size={16} />
            Browse Course Catalog
          </ActionButton>
          {payment.receiptUrl && (
            <ActionButton onClick={handleDownloadReceipt}>
              <FiDownload size={16} />
              Download receipt
            </ActionButton>
          )}
        </ButtonGroup>
      </PageStack>
    </Container>
  );
}

export default PurchaseConfirmation;
